import { IDevice } from "../types";

export interface TooltipPosition {
  direction: "top" | "bottom" | "left" | "right" | "top-left" | "top-right" | "bottom-left" | "bottom-right";
  offset: [number, number];
}

export interface DeviceWithTooltipPosition extends IDevice {
  tooltipPosition: TooltipPosition;
}

// Constants for tooltip positioning
const TOOLTIP_MIN_DISTANCE = 100; // Distance threshold for detecting nearby devices
const MAX_OFFSET = 15; // Maximum offset distance in pixels

// Default offsets for each direction - kept very close (10-15px)
const DEFAULT_OFFSETS: Record<string, [number, number]> = {
  top: [0, -12],
  bottom: [0, 12],
  left: [-12, 0],
  right: [12, 0],
  "top-left": [-10, -10],
  "top-right": [10, -10],
  "bottom-left": [-10, 10],
  "bottom-right": [10, 10],
};

// All available directions for tooltip placement
const ALL_DIRECTIONS = ["top", "right", "bottom", "left", "top-right", "bottom-right", "bottom-left", "top-left"] as const;
type Direction = typeof ALL_DIRECTIONS[number];

// Estimated tooltip dimensions for collision detection
const TOOLTIP_WIDTH = 80; // Approximate width in pixels
const TOOLTIP_HEIGHT = 30; // Approximate height in pixels

/**
 * Simpler approach: Check if two devices are too close together
 * If they are, we'll position their tooltips differently
 */
function devicesAreTooClose(
  device1: IDevice,
  device2: IDevice,
  minDistance: number = TOOLTIP_MIN_DISTANCE
): boolean {
  if (!device1.position || !device2.position) return false;

  const dx = device1.position.x - device2.position.x;
  const dy = device1.position.y - device2.position.y;
  const distance = Math.sqrt(dx * dx + dy * dy);

  return distance < minDistance;
}

/**
 * Calculate tooltip bounding box for collision detection
 */
function getTooltipBounds(
  devicePosition: { x: number; y: number },
  direction: Direction,
  offset: [number, number]
): { x: number; y: number; width: number; height: number } {
  const tooltipX = devicePosition.x + offset[0];
  const tooltipY = devicePosition.y + offset[1];

  // Adjust position based on direction to account for tooltip anchor point
  let x = tooltipX;
  let y = tooltipY;

  if (direction.includes("top")) {
    y = tooltipY - TOOLTIP_HEIGHT;
  } else if (direction.includes("bottom")) {
    y = tooltipY;
  } else {
    y = tooltipY - TOOLTIP_HEIGHT / 2;
  }

  if (direction.includes("left")) {
    x = tooltipX - TOOLTIP_WIDTH;
  } else if (direction.includes("right")) {
    x = tooltipX;
  } else {
    x = tooltipX - TOOLTIP_WIDTH / 2;
  }

  return { x, y, width: TOOLTIP_WIDTH, height: TOOLTIP_HEIGHT };
}

/**
 * Check if two bounding boxes overlap
 */
function boundsOverlap(
  bounds1: { x: number; y: number; width: number; height: number },
  bounds2: { x: number; y: number; width: number; height: number }
): boolean {
  return !(
    bounds1.x + bounds1.width < bounds2.x ||
    bounds2.x + bounds2.width < bounds1.x ||
    bounds1.y + bounds1.height < bounds2.y ||
    bounds2.y + bounds2.height < bounds1.y
  );
}

/**
 * Find the best direction for a device's tooltip based on nearby devices
 * Uses actual collision detection with bounding boxes
 */
function findBestDirection(
  device: IDevice,
  nearbyDevices: IDevice[],
  assignedPositions: Map<string, { direction: Direction; offset: [number, number] }>
): Direction {
  if (!device.position) return "top";

  // Try each direction and check for collisions
  const directionScores: Record<Direction, number> = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    "top-left": 0,
    "top-right": 0,
    "bottom-left": 0,
    "bottom-right": 0,
  };

  // For each possible direction, calculate collision score
  ALL_DIRECTIONS.forEach(direction => {
    const offset = DEFAULT_OFFSETS[direction];
    const myBounds = getTooltipBounds(device.position!, direction, offset);

    // Check collisions with already positioned tooltips
    assignedPositions.forEach((pos, mac) => {
      if (mac === device.mac) return;

      const otherDevice = nearbyDevices.find(d => d.mac === mac);
      if (!otherDevice?.position) return;

      const otherBounds = getTooltipBounds(otherDevice.position, pos.direction, pos.offset);

      if (boundsOverlap(myBounds, otherBounds)) {
        directionScores[direction] += 10; // Heavy penalty for collision
      }
    });

    // Also check proximity to nearby devices (prefer opposite directions)
    nearbyDevices.forEach(nearby => {
      if (!nearby.position || nearby.mac === device.mac) return;

      const dx = nearby.position.x - device.position!.x;
      const dy = nearby.position.y - device.position!.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > TOOLTIP_MIN_DISTANCE) return;

      const weight = (TOOLTIP_MIN_DISTANCE - distance) / TOOLTIP_MIN_DISTANCE;
      const angle = Math.atan2(dy, dx);
      const degrees = (angle * 180 / Math.PI + 360) % 360;

      // Penalize directions toward nearby devices
      if (degrees >= 337.5 || degrees < 22.5) {
        if (direction.includes("right")) directionScores[direction] += weight;
      } else if (degrees >= 22.5 && degrees < 67.5) {
        if (direction === "bottom-right") directionScores[direction] += weight;
      } else if (degrees >= 67.5 && degrees < 112.5) {
        if (direction.includes("bottom")) directionScores[direction] += weight;
      } else if (degrees >= 112.5 && degrees < 157.5) {
        if (direction === "bottom-left") directionScores[direction] += weight;
      } else if (degrees >= 157.5 && degrees < 202.5) {
        if (direction.includes("left")) directionScores[direction] += weight;
      } else if (degrees >= 202.5 && degrees < 247.5) {
        if (direction === "top-left") directionScores[direction] += weight;
      } else if (degrees >= 247.5 && degrees < 292.5) {
        if (direction.includes("top")) directionScores[direction] += weight;
      } else {
        if (direction === "top-right") directionScores[direction] += weight;
      }
    });
  });

  // Choose the direction with the lowest score
  const sortedDirections = Object.entries(directionScores).sort((a, b) => a[1] - b[1]);
  return sortedDirections[0][0] as Direction;
}

/**
 * Simple and effective tooltip positioning using collision detection
 */
function getTooltipPositionForDevice(
  device: IDevice,
  allDevices: IDevice[],
  assignedPositions: Map<string, { direction: Direction; offset: [number, number] }>
): TooltipPosition {
  // Find nearby devices that might cause conflicts
  const nearbyDevices = allDevices.filter(other =>
    other.mac !== device.mac && devicesAreTooClose(device, other)
  );

  // If no nearby devices, use default top position
  if (nearbyDevices.length === 0) {
    return { direction: "top", offset: DEFAULT_OFFSETS.top };
  }

  // Find the best direction based on collision detection
  const bestDirection = findBestDirection(device, nearbyDevices, assignedPositions);

  // Use the default offset for the chosen direction
  const offset = DEFAULT_OFFSETS[bestDirection];

  return { direction: bestDirection, offset };
}

/**
 * Calculate optimal tooltip positions for all devices using collision detection
 */
export function calculateTooltipPositions(
  devices: IDevice[],
  mapBounds?: { width: number; height: number }
): DeviceWithTooltipPosition[] {
  // Filter out devices without valid positions
  const validDevices = devices.filter(
    (device) =>
      device.position &&
      typeof device.position.x === "number" &&
      typeof device.position.y === "number" &&
      !isNaN(device.position.x) &&
      !isNaN(device.position.y)
  );

  // Track assigned positions for collision detection
  const assignedPositions = new Map<string, { direction: Direction; offset: [number, number] }>();

  // Sort devices by position (top-left to bottom-right) for consistent processing
  const sortedDevices = [...validDevices].sort((a, b) => {
    if (!a.position || !b.position) return 0;
    const yDiff = a.position.y - b.position.y;
    if (Math.abs(yDiff) > 10) return yDiff;
    return a.position.x - b.position.x;
  });

  // Calculate tooltip position for each device based on collision detection
  return sortedDevices.map((device) => {
    const position = getTooltipPositionForDevice(device, validDevices, assignedPositions);
    assignedPositions.set(device.mac, { direction: position.direction, offset: position.offset });

    return {
      ...device,
      tooltipPosition: position,
    };
  });
}

/**
 * Calculate distance between two devices
 */
function getDeviceDistance(device1: IDevice, device2: IDevice): number {
  if (!device1.position || !device2.position) return Infinity;

  const dx = device1.position.x - device2.position.x;
  const dy = device1.position.y - device2.position.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Find nearby devices that might cause tooltip conflicts
 */
function findNearbyDevices(
  device: IDevice,
  allDevices: IDevice[],
  maxDistance: number = TOOLTIP_MIN_DISTANCE
): IDevice[] {
  return allDevices.filter((otherDevice) => {
    if (otherDevice.mac === device.mac) return false;
    return getDeviceDistance(device, otherDevice) <= maxDistance;
  });
}

/**
 * Utility function to check if devices are clustered (for debugging/analytics)
 */
export function getDeviceClusterInfo(devices: IDevice[]): {
  totalDevices: number;
  clusteredDevices: number;
  clusters: Array<{ devices: IDevice[]; center: { x: number; y: number } }>;
} {
  const validDevices = devices.filter(
    (device) => device.position && !isNaN(device.position.x) && !isNaN(device.position.y)
  );

  const clusters: Array<{ devices: IDevice[]; center: { x: number; y: number } }> = [];
  const processed = new Set<string>();

  for (const device of validDevices) {
    if (processed.has(device.mac)) continue;

    const nearby = findNearbyDevices(device, validDevices, TOOLTIP_MIN_DISTANCE);
    if (nearby.length > 0) {
      const clusterDevices = [device, ...nearby];

      // Calculate cluster center
      const center = {
        x: clusterDevices.reduce((sum, d) => sum + d.position!.x, 0) / clusterDevices.length,
        y: clusterDevices.reduce((sum, d) => sum + d.position!.y, 0) / clusterDevices.length,
      };

      clusters.push({ devices: clusterDevices, center });

      // Mark all devices in this cluster as processed
      clusterDevices.forEach(d => processed.add(d.mac));
    } else {
      processed.add(device.mac);
    }
  }

  return {
    totalDevices: validDevices.length,
    clusteredDevices: clusters.reduce((sum, cluster) => sum + cluster.devices.length, 0),
    clusters,
  };
}
